<template>
    <div class="relative mt-5" ref="wrapper">
        <input
            ref="input"
            type="text"
            v-model="search"
            @focus="onFocus"
            @input="onInput"
            :name="name"
            :disabled="disabled"
            :readonly="readonly"
            :placeholder="label"
            :class="[
                typeof styling !== 'undefined' ? styling : '',
                validation?.$error ? 'border-red-500 border-2' : '',
            ]"
            class="textInput peer h-10 p-2 focus:ring-0 w-full border-gray-300 text-gray-900 placeholder-transparent focus:outline-none focus:border-ap-primary"
            autocomplete="off"
        />
        <label class="textInputLabel peer-placeholder-shown:pl-3 pointer-events-none absolute left-0 text-gray-600 text-sm transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-2 -top-4 pl-0">{{ label }}</label>

        <div
            v-if="open"
            class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto"
        >
            <div
                v-for="option in filteredOptions"
                :key="option"
                @mousedown.prevent="selectOption(option)"
                class="px-3 py-2 cursor-pointer hover:bg-blue-100 text-sm"
                :class="{ 'bg-blue-50 font-semibold': option === modelValue }"
            >{{ option }}</div>
            <div v-if="filteredOptions.length === 0" class="px-3 py-2 text-gray-400 text-sm">No matches</div>
        </div>
    </div>
    <small class="text-sm text-red-500" v-if="validation?.$error">{{ validation.$errors[0].$message }}</small>
</template>

<script>
export default {
    data() {
        return {
            search: this.modelValue ?? '',
            open: false,
            typing: false,
        };
    },
    props: {
        options: { type: Array },
        optionSlug: { type: String },
        label: { type: String, required: true },
        name: { type: String, required: false },
        value: { required: false },
        modelValue: { type: String, required: false },
        readonly: { type: Boolean, required: false },
        disabled: { type: Boolean, required: false },
        styling: { required: false },
        validation: { type: Object, required: false },
        // When true, only a value from the options list is accepted; free text
        // that doesn't match an option is discarded on blur.
        strict: { type: Boolean, default: false },
    },
    emits: ['update:modelValue', 'blur'],
    computed: {
        filteredOptions() {
            if (!this.typing || !this.search) return this.options;
            const q = this.search.toLowerCase();
            return this.options.filter(o => o.toLowerCase().includes(q));
        },
    },
    watch: {
        modelValue(val) {
            if (!this.open) {
                this.search = val ?? '';
            }
        },
    },
    methods: {
        onFocus() {
            this.typing = false;
            this.open = true;
        },
        onInput() {
            this.typing = true;
            this.open = true;
        },
        selectOption(option) {
            this.search = option;
            this.open = false;
            this.typing = false;
            this.$emit('update:modelValue', option);
            this.$nextTick(() => {
                this.$emit('blur');
            });
        },
        onClickOutside(e) {
            if (this.$refs.wrapper && !this.$refs.wrapper.contains(e.target)) {
                this.open = false;
                this.commit();
            }
        },
        commit() {
            // In strict mode only accept a value that matches an option; anything
            // else is discarded so the make can only be a pre-defined one.
            if (this.strict) {
                const match = (this.options || []).find(
                    o => o.toLowerCase() === String(this.search).trim().toLowerCase(),
                );

                if (match) {
                    this.search = match;
                    if (match !== this.modelValue) {
                        this.$emit('update:modelValue', match);
                        this.$emit('blur');
                    }
                    return;
                }

                // No match - revert to the last valid value.
                this.search = this.modelValue ?? '';
                return;
            }

            if (this.search !== this.modelValue) {
                this.$emit('update:modelValue', this.search);
                this.$emit('blur');
            }
        },
    },
    mounted() {
        document.addEventListener('click', this.onClickOutside);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.onClickOutside);
    },
};
</script>
